// import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';

let handleError: HandleClientError | undefined;

// Determine environment based on URL
const getEnvironment = (): string => {
	const apiUrl = alumaConfig.alumaApiUrl;

	if (apiUrl.includes('api.loanlogics.aluma.io')) return 'loanlogics';
	if (apiUrl.includes('api.us.aluma.io')) return 'us';
	if (apiUrl.includes('api.aluma.io')) return 'gb';

	// Local dev (npm run dev) or anything else that isn't a known
	// production API. When Sentry is force-enabled via VITE_SENTRY_ENABLED
	// we want these events tagged as 'local' so they can be filtered /
	// discarded in the Sentry UI.
	return 'local';
};

// Determine if running against a real production API
const isProdEnvironment = (): boolean => getEnvironment() !== 'local';

// Allow turning Sentry on in local dev for testing. Set
// VITE_SENTRY_ENABLED=true (see the `dev:sentry` npm script) to emit
// events against the real project tagged with environment='local'.
const isSentryForcedOn = (): boolean => import.meta.env.VITE_SENTRY_ENABLED === 'true';

const shouldInitSentry = (): boolean => isProdEnvironment() || isSentryForcedOn();

if (shouldInitSentry()) {
	// Dynamic import of Sentry
	import('@sentry/sveltekit').then((Sentry) => {
		Sentry.init({
			dsn: 'https://ded10f254a735cb291b3b2b23585af77@o4510391140089856.ingest.us.sentry.io/4510391200514048',
			environment: getEnvironment(),
			enabled: shouldInitSentry(),

			// If you don't want to use any of the integrations below, just remove them
			integrations: [
				Sentry.captureConsoleIntegration({
					levels: ['error']
				}),
				Sentry.replayIntegration({
					maskAllInputs: true,
					blockAllMedia: true,
					// networkDetailAllowUrls: [
					// 	/.*aluma.*/ // Match all URLs with `aluma` in it
					// ],
					networkCaptureBodies: false
				})
				// With `browserProfilingIntegration`, Chrome will incorrectly attribute regular
				// rendering work as "Profiling Overhead" if you are doing local profiling via
				// the Chrome DevTools Performance panel. To avoid this, disable or remove the
				// integration when profiling with Chrome DevTools.
				// Sentry.browserProfilingIntegration() // < disabled until we're sure it doesn't cause performance issues
			],

			// Un-comment to filter out all console logs from being sent to Sentry.
			// Please note that even if this is enabled, console warnings and errors will still be sent.
			// beforeBreadcrumb: (breadcrumb) => {
			// 	if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
			// 		return null;
			// 	}
			// 	return breadcrumb;
			// },

			// Enable logs to be sent to Sentry
			enableLogs: true,
			// Enable sending user PII (Personally Identifiable Information)
			// https://docs.sentry.io/platforms/javascript/guides/sveltekit/configuration/options/#sendDefaultPii
			sendDefaultPii: false,

			// It's recommended to set `tracesSampleRate` to 1.0 in development and sample at a lower rate in production.
			tracesSampleRate: isProdEnvironment() ? 0.1 : 1.0,
			// Since `profilesSampleRate` is relative to `tracesSampleRate`,
			// the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
			// For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
			// result in 25% of transactions being profiled (0.5*0.5=0.25)
			profilesSampleRate: 1.0,
			// This sets the sample rate to be 0%. You may want this to be 100% while
			// in development and sample at a lower rate in production
			replaysSessionSampleRate: 0.0,
			// If the entire session is not sampled, use the below sample rate to sample
			// sessions when an error occurs.
			replaysOnErrorSampleRate: 1.0
		});

		// If you have a custom error handler, pass it to `handleErrorWithSentry`
		handleError = Sentry.handleErrorWithSentry() as HandleClientError;
	});
}

export { handleError };
